import {Button, CounterLabel, Spinner, Tooltip} from '@primer/react'
import {AutomatedReviewDiffEntry} from './AutomatedReviewDiffEntry'
import React, {useState, useRef, useCallback} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ApplySuggestionDialog} from '../CodeSuggestionActions/ApplySuggestionDialog'
import {useApplyAutomatedSuggestionMutation} from '@github-ui/pull-requests/use-apply-automated-suggestion-mutation'
import type {AutomatedReviewSuggestionProps} from './AutomatedReviewSuggestion'
import {FoldIcon, UnfoldIcon} from '@primer/octicons-react'
import type {Suggestion} from '../../../conversation-types'
import styles from './AutomatedReviewSuggestionStates.module.css'
import {noop} from '@github-ui/noop'

interface AutomatedReviewSuggestionBaseProps {
  suggestion: Suggestion
  actions?: React.ReactNode
}

function AutomatedReviewSuggestionBase({suggestion, actions}: AutomatedReviewSuggestionBaseProps) {
  const diffEntries = suggestion.diffEntries || []
  const suggestedChangesCount = diffEntries.length || 0

  return (
    <div
      className={`d-flex flex-column color-fg-default ${styles.suggestion}`}
      data-testid="automated-review-suggestion"
    >
      {/* Metadata */}
      {suggestedChangesCount > 0 && (
        <div className="d-flex flex-items-center flex-justify-between text-small color-fg-muted pt-2">
          <div>
            Suggested changeset <CounterLabel scheme="secondary">{suggestedChangesCount}</CounterLabel>
          </div>
        </div>
      )}

      {/* Diffs */}
      {diffEntries.map(diffEntry => {
        return <AutomatedReviewDiffEntry key={diffEntry.path} diffEntry={diffEntry} />
      })}

      {actions}
    </div>
  )
}

export function AutomatedReviewSuggestionPending(_props: AutomatedReviewSuggestionProps) {
  return (
    <div className={styles.suggestionPending} data-testid="automated-review-suggestion-pending">
      <Spinner size="medium" />
      Loading suggestion...
    </div>
  )
}

// TODO(@aliceclv): we store the suggestion_error_message in the DB, we can display it here
export function AutomatedReviewSuggestionError(_props: AutomatedReviewSuggestionProps) {
  return (
    <div className={styles.suggestionError} data-testid="automated-review-suggestion-error">
      <h3 className={styles.suggestionErrorTitle}>Sorry, we could not generate an autofix suggestion.</h3>
      <p className="color-fg-muted">
        Try pushing a new commit or if the problem persists{' '}
        <a className="fgColor-link" href="https://support.github.com/">
          <u>contact support</u>
        </a>
        .
      </p>
    </div>
  )
}

export function AutomatedReviewSuggestionOutdated({suggestion, isClassicExperience}: AutomatedReviewSuggestionProps) {
  const Actions = (
    <div className="d-flex flex-items-center flex-justify-between gap-1">
      <span className="color-fg-muted text-small">Unable to commit as this autofix suggestion is now outdated.</span>

      {isClassicExperience ? (
        <Button size="small" variant="primary" disabled>
          Commit suggestion
        </Button>
      ) : (
        <Tooltip text="This autofix suggestion is outdated">
          <Button variant="default" inactive>
            Apply suggestion
          </Button>
        </Tooltip>
      )}
    </div>
  )
  return suggestion && <AutomatedReviewSuggestionBase suggestion={suggestion} actions={Actions} />
}

export function AutomatedReviewSuggestionApplied({suggestion}: AutomatedReviewSuggestionProps) {
  const [isFolded, setIsFolded] = useState(true)
  const fold = () => setIsFolded(!isFolded)

  return (
    <div className={styles.suggestionApplied}>
      <div className="d-flex flex-justify-between flex-items-center">
        <span className={styles.suggestionAppliedText}>This autofix suggestion was applied.</span>
        <Button
          variant="invisible"
          onClick={fold}
          leadingVisual={isFolded ? UnfoldIcon : FoldIcon}
          size="small"
          className="color-fg-muted"
        >
          {isFolded ? 'Show' : 'Hide'} autofix suggestion
        </Button>
      </div>
      {!isFolded && suggestion && <AutomatedReviewSuggestionBase suggestion={suggestion} />}
    </div>
  )
}

export function AutomatedReviewSuggestionPresent({
  automatedCommentId,
  basePath,
  commentAuthorLogin,
  commentTitle,
  isClassicExperience,
  suggestion,
}: AutomatedReviewSuggestionProps) {
  const [isApplySuggestionDialogOpen, setIsApplySuggestionDialogOpen] = useState(false)

  const diffEntries = suggestion?.diffEntries || []
  const suggestedChangesCount = diffEntries.length || 0
  const buttonRef = useRef<HTMLButtonElement>(null)
  const buttonLabel = isClassicExperience ? 'Commit suggestion' : 'Apply suggestion'
  // Note(@aliceclv): this is optimized for Universe ship & code quality
  // We should have a default commit message passed from the backend when adapting the component to code scanning & CCR
  const defaultCommitMessage = `Potential fix for pull request finding${commentTitle ? ` '${commentTitle}'` : ''}`
  const {mutate: applyAutomatedSuggestion} = useApplyAutomatedSuggestionMutation(automatedCommentId, basePath)

  const onApplySuggestionDialogClose = useCallback(() => setIsApplySuggestionDialogOpen(false), [])
  const handleCommitSuggestion = useCallback(
    (
      commitMessage: string,
      onError: (error: Error, type?: string, friendlyMessage?: string) => void,
      onCompleted: () => void,
    ) => {
      applyAutomatedSuggestion(
        {
          message: commitMessage,
        },
        {
          onSuccess: () => {
            onApplySuggestionDialogClose()
            onCompleted()
          },
          onError: (error: Error) => {
            onError(error, 'applyAutomatedSuggestion', 'Failed to commit suggested changes')
          },
        },
      )
    },
    [applyAutomatedSuggestion, onApplySuggestionDialogClose],
  )

  const batchSuggestedChangesEnabled = isFeatureEnabled('batch_suggested_changes')

  // TODO: Implement batch functionality for automated review suggestions
  // https://github.com/github/copilot-code-review/issues/800
  const handleAddToBatch = noop

  const Actions = (
    <>
      <div className="d-flex gap-2 flex-items-center flex-justify-end flex-wrap">
        <Button
          data-testid="commit-suggestion-button"
          size={isClassicExperience ? 'small' : undefined}
          variant={isClassicExperience ? 'primary' : 'default'}
          ref={buttonRef}
          onClick={() => setIsApplySuggestionDialogOpen(!isApplySuggestionDialogOpen)}
        >
          {buttonLabel}
        </Button>
        {batchSuggestedChangesEnabled &&
          (isClassicExperience ? (
            <Tooltip text="Batching autofix suggestions is only available in the new Files changed experience">
              <Button data-testid="add-suggestion-to-batch-button" size="small" inactive>
                Add suggestion to batch
              </Button>
            </Tooltip>
          ) : (
            <Button data-testid="add-suggestion-to-batch-button" variant="primary" onClick={handleAddToBatch}>
              Add suggestion to batch
            </Button>
          ))}
      </div>

      {/* Commit suggestion dialog */}
      {isApplySuggestionDialogOpen && suggestedChangesCount > 0 && (
        <ApplySuggestionDialog
          authorLogins={commentAuthorLogin ? [commentAuthorLogin] : []}
          batchSize={suggestedChangesCount}
          defaultCommitMessage={defaultCommitMessage}
          onClose={onApplySuggestionDialogClose}
          onCommit={handleCommitSuggestion}
          dialogTitle={buttonLabel}
        />
      )}
    </>
  )

  return suggestion && <AutomatedReviewSuggestionBase suggestion={suggestion} actions={Actions} />
}
